import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./api";
import { tr } from "../i18n";
import { history } from "./history";

const kindOf = (c) => (c?.type === "image" ? "photo" : ["comment", "callout", "note", "text"].includes(c?.type) ? "comment" : "shape");
const updLabel = (c, patch) => tr(patch.text !== undefined ? "h_c_text" : patch.geom ? `h_${kindOf(c)}_move` : patch.style ? "h_c_style" : patch.resolved !== undefined ? "h_c_resolve" : "h_c_edit");

// Shared, auto-saved PDF comments for a project (polls so collaborators see updates).
export function useComments(projectId, me, role) {
    const [comments, setComments] = useState([]);
    const base = `/projects/${projectId}/comments`;
    const fail = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("msg_comment_save_failed"));
    const put = (c) => setComments((list) => list.map((x) => (x.id === c.id ? c : x)));

    const load = useCallback(async () => {
        try { const { data } = await api.get(base); setComments(data); } catch { /* offline */ }
    }, [base]);

    useEffect(() => {
        if (!projectId) return;
        load();
        const iv = setInterval(load, 15000);
        return () => clearInterval(iv);
    }, [projectId, load]);

    const listRef = useRef([]);
    listRef.current = comments;
    const lastRef = useRef(null);
    // Coalesce rapid same-kind operations (multi-delete, typing, multi-style) into one undo step.
    const record = (key, entry, merge) => {
        const l = lastRef.current, now = Date.now();
        if (l && l.key === key && now - l.at < 1500 && history.last() === l.entry) {
            const m = merge(l.entry);
            history.replaceLast(m);
            lastRef.current = { key, at: now, entry: m };
            return;
        }
        const e = history.push(entry);
        if (e) lastRef.current = { key, at: now, entry: e };
    };
    const restore = async (c) => {
        try { const { data } = await api.post(`${base}/restore`, { comment: c }); setComments((l) => (l.some((x) => x.id === data.id) ? l : [...l, data])); } catch (e) { fail(e); }
    };
    const rawUpdate = async (id, patch) => {
        setComments((l) => l.map((x) => (x.id === id ? { ...x, ...patch } : x)));
        try { const { data } = await api.patch(`${base}/${id}`, patch); put(data); } catch (e) { fail(e); load(); }
    };
    const rawRemove = async (id) => {
        setComments((l) => l.filter((x) => x.id !== id));
        try { await api.delete(`${base}/${id}`); } catch (e) { fail(e); load(); }
    };

    const create = async (c) => {
        try {
            const { data } = await api.post(base, c);
            setComments((l) => [...l, data]);
            history.push({ label: tr(`h_${kindOf(data)}_add`), undo: () => rawRemove(data.id), redo: () => restore(data) });
            return data;
        } catch (e) { fail(e); return null; }
    };
    const update = async (id, patch) => {
        const prev = listRef.current.find((x) => x.id === id);
        if (prev) {
            const before = Object.fromEntries(Object.keys(patch).map((k) => [k, prev[k] ?? (k === "text" ? "" : null)]));
            const one = (b, p) => ({ label: updLabel(prev, p), undo: () => rawUpdate(id, b), redo: () => rawUpdate(id, p), parts: [{ id, b, p }] });
            const key = `u:${Object.keys(patch).sort().join(",")}:${patch.style || patch.geom ? "*" : id}`;
            record(key, one(before, patch), (last) => {
                const parts = [...(last.parts || [])];
                const i = parts.findIndex((x) => x.id === id);
                if (i >= 0) parts[i] = { ...parts[i], p: patch }; else parts.push({ id, b: before, p: patch });
                return { label: last.label, parts, undo: () => Promise.all(parts.map((x) => rawUpdate(x.id, x.b))), redo: () => Promise.all(parts.map((x) => rawUpdate(x.id, x.p))) };
            });
        }
        await rawUpdate(id, patch);
    };
    const remove = async (id) => {
        const prev = listRef.current.find((x) => x.id === id);
        if (prev) {
            const entry = (list) => ({ label: tr(list.length > 1 ? "h_c_del_n" : `h_${kindOf(list[0])}_del`, { n: list.length }), list,
                undo: () => Promise.all(list.map(restore)), redo: () => Promise.all(list.map((x) => rawRemove(x.id))) });
            record("del", entry([prev]), (last) => entry([...(last.list || []), prev]));
        }
        await rawRemove(id);
    };
    const reply = async (id, text) => {
        try { const { data } = await api.post(`${base}/${id}/replies`, { text }); put(data); } catch (e) { fail(e); }
    };
    const removeReply = async (id, rid) => {
        try { const { data } = await api.delete(`${base}/${id}/replies/${rid}`); put(data); } catch (e) { fail(e); }
    };
    const canEdit = (c) => role === "owner" || c?.author?.user_id === me?.user_id;
    const canEditReply = (r) => role === "owner" || r?.author?.user_id === me?.user_id;
    const uploadImage = async (file) => {
        const fd = new FormData();
        fd.append("file", file, file.name || "image.png");
        try { const { data } = await api.post(`/projects/${projectId}/comment-images`, fd); return data.name; } catch (e) { fail(e); return null; }
    };

    return { projectId, comments, create, update, remove, reply, removeReply, canEdit, canEditReply, uploadImage, reload: load, rawRemove, restore };
}
